import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, 
  Eye,
  Users,
  Phone,
  Clock,
  Loader2,
  XCircle,
  CheckCircle,
  MessageSquare,
  ExternalLink,
  RefreshCw,
  Package,
  Truck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { safeFetch } from '../../utils/safeFetch';

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  viewed: { label: 'Viewed', color: 'bg-purple-100 text-purple-700' },
  contacted: { label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600' }
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [statusFilter]);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API_URL}/api/leads/`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }

      const result = await safeFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!result.ok) throw new Error('Failed to fetch leads');
      setLeads(Array.isArray(result.data) ? result.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!result.ok) throw new Error(result.data?.detail || 'Failed to update status');
      
      await fetchLeads();
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(result.data);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const resendSMS = async (leadId) => {
    setResendingId(leadId);
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/leads/${leadId}/resend-sms`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!result.ok) throw new Error('Failed to resend SMS');
      
      if (result.data?.sms_sent) {
        alert('SMS sent successfully! Link extended for 10 more minutes.');
      } else {
        alert('SMS not configured. Link extended for 10 more minutes.');
      }
      
      await fetchLeads();
    } catch (err) {
      alert(err.message);
    } finally {
      setResendingId(null);
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(search) ||
      lead.mobile?.includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Australia/Melbourne'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return true;
    return new Date() > new Date(expiresAt);
  };

  const getQuoteUrl = (token) => {
    const origin = window.location.origin;
    return `${origin}/quote/${token}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D9488]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Leads</h1>
        <p className="text-[#64748B] mt-1">Manage pricing inquiries and leads</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(statusConfig).map(([key, config]) => {
          const count = leads.filter(l => l.status === key).length;
          return (
            <Card key={key} className="bg-white border-[#E2E8F0]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#111111]">{count}</p>
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
                  {config.label}
                </span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
          <Input
            type="text"
            placeholder="Search by name or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-[#E2E8F0] text-[#111111] placeholder:text-[#94A3B8]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-white border border-[#E2E8F0] rounded-lg text-[#111111] focus:border-[#0D9488] outline-none"
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="viewed">Viewed</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Leads Table */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Lead</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Pricing Link</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">SMS</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-[#64748B]">
                    <Users className="w-12 h-12 mx-auto mb-3 text-[#E2E8F0]" />
                    <p>No leads found</p>
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => {
                  const status = statusConfig[lead.status] || statusConfig.new;
                  const expired = isExpired(lead.expires_at);
                  
                  return (
                    <tr key={lead.id} className="hover:bg-[#F8FAFC] transition-colors">
                      <td className="px-4 py-4">
                        <p className="text-[#111111] font-medium">{lead.name}</p>
                        <p className="text-[#94A3B8] text-sm flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.mobile}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          lead.service_interest === 'pickup' 
                            ? 'bg-[#F0FDFA] text-[#0D9488]' 
                            : 'bg-blue-50 text-blue-600'
                        }`}>
                          {lead.service_interest === 'pickup' ? <Package className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                          {lead.service_interest === 'pickup' ? 'Collection' : 'Delivery'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                        {lead.pricing_viewed && (
                          <p className="text-[#94A3B8] text-xs mt-1 flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            Viewed
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${expired ? 'bg-red-500' : 'bg-green-500'}`} />
                          <span className={`text-xs ${expired ? 'text-red-600' : 'text-green-600'}`}>
                            {expired ? 'Expired' : 'Active'}
                          </span>
                        </div>
                        <a 
                          href={getQuoteUrl(lead.token)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#0D9488] text-xs hover:underline flex items-center gap-1 mt-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="px-4 py-4">
                        {lead.sms_sent ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Sent
                          </span>
                        ) : (
                          <span className="text-[#94A3B8] text-xs">Not sent</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-[#64748B] text-sm">{formatDate(lead.created_at)}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#0D9488] hover:text-[#0D9488] hover:bg-[#F0FDFA]"
                            onClick={() => setSelectedLead(lead)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:text-blue-600 hover:bg-blue-50"
                            onClick={() => resendSMS(lead.id)}
                            disabled={resendingId === lead.id}
                          >
                            {resendingId === lead.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4">
          <Card className="bg-white border-[#E2E8F0] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#111111]">Lead Details</h2>
                <button 
                  onClick={() => setSelectedLead(null)}
                  className="text-[#94A3B8] hover:text-[#111111]"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Lead Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                    <p className="text-[#64748B] text-sm mb-1">Name</p>
                    <p className="text-[#111111] font-medium">{selectedLead.name}</p>
                  </div>

                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                    <p className="text-[#64748B] text-sm mb-1">Mobile</p>
                    <p className="text-[#111111] font-medium">{selectedLead.mobile}</p>
                  </div>

                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                    <p className="text-[#64748B] text-sm mb-1">Service Interest</p>
                    <p className="text-[#111111] font-medium capitalize">
                      {selectedLead.service_interest === 'pickup' ? 'Packaging Collection' : 'Delivery Service'}
                    </p>
                  </div>

                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                    <p className="text-[#64748B] text-sm mb-1">Pricing Viewed</p>
                    <p className="text-[#111111] font-medium">
                      {selectedLead.pricing_viewed ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" /> Yes
                        </span>
                      ) : 'No'}
                    </p>
                    {selectedLead.pricing_viewed_at && (
                      <p className="text-[#94A3B8] text-xs mt-1">
                        at {formatDate(selectedLead.pricing_viewed_at)}
                      </p>
                    )}
                  </div>

                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0] md:col-span-2">
                    <p className="text-[#64748B] text-sm mb-1">Personalized Pricing Link</p>
                    <div className="flex items-center gap-2 mt-2">
                      <input 
                        type="text" 
                        value={getQuoteUrl(selectedLead.token)}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-[#E2E8F0] rounded text-sm text-[#111111]"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigator.clipboard.writeText(getQuoteUrl(selectedLead.token))}
                      >
                        Copy
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`w-2 h-2 rounded-full ${isExpired(selectedLead.expires_at) ? 'bg-red-500' : 'bg-green-500'}`} />
                      <span className={`text-xs ${isExpired(selectedLead.expires_at) ? 'text-red-600' : 'text-green-600'}`}>
                        {isExpired(selectedLead.expires_at) ? 'Expired' : `Active until ${formatDate(selectedLead.expires_at)}`}
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                    <p className="text-[#64748B] text-sm mb-1">SMS Status</p>
                    <p className="text-[#111111]">
                      {selectedLead.sms_sent ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Sent
                        </span>
                      ) : (
                        <span className="text-[#94A3B8]">Not sent (Twilio not configured)</span>
                      )}
                    </p>
                  </div>

                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                    <p className="text-[#64748B] text-sm mb-1">Created</p>
                    <p className="text-[#111111]">{formatDate(selectedLead.created_at)}</p>
                  </div>
                </div>

                {/* Update Status */}
                <div className="border-t border-[#E2E8F0] pt-6">
                  <p className="text-[#111111] font-medium mb-3">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <Button
                        key={key}
                        size="sm"
                        variant={selectedLead.status === key ? 'default' : 'outline'}
                        className={
                          selectedLead.status === key 
                            ? 'bg-[#0D9488] hover:bg-[#0F766E] text-white border-0' 
                            : 'border-[#E2E8F0] text-[#64748B] hover:text-[#111111] hover:bg-[#F8FAFC]'
                        }
                        onClick={() => updateLeadStatus(selectedLead.id, key)}
                        disabled={isUpdating || selectedLead.status === key}
                      >
                        {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : config.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-[#E2E8F0] pt-6 flex gap-3">
                  <Button
                    variant="outline"
                    className="border-[#E2E8F0]"
                    onClick={() => resendSMS(selectedLead.id)}
                    disabled={resendingId === selectedLead.id}
                  >
                    {resendingId === selectedLead.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <MessageSquare className="w-4 h-4 mr-2" />
                    )}
                    Resend SMS & Extend Link
                  </Button>
                  <a 
                    href={getQuoteUrl(selectedLead.token)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="border-[#E2E8F0]">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Pricing Page
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
